/**
 * Staff console shell. Same persistence contract as the tenant shell — the
 * plum rail mounts once — and the same authorization posture: the guard asks
 * /admin/me once for identity, and every subsequent request is re-verified
 * server-side against platform_admins. Staff and tenants stay separate
 * identities all the way down; this shell never touches /api/me.
 */
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Navigate, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { ApiError, signOut } from './api';
import { adminApi, type AdminMe } from './adminApi';

const AdminContext = createContext<AdminMe['admin'] | null>(null);

export function useAdmin() {
  const value = useContext(AdminContext);
  if (!value) throw new Error('useAdmin outside the admin shell');
  return value;
}

type GuardState =
  | { status: 'loading' }
  | { status: 'denied'; reason: string }
  | { status: 'ready'; admin: AdminMe['admin'] };

function RequireAdmin({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GuardState>({ status: 'loading' });

  useEffect(() => {
    adminApi
      .me()
      .then((data) => setState({ status: 'ready', admin: data.admin }))
      .catch((error) => {
        const reason =
          error instanceof ApiError
            ? error.status === 403
              ? 'forbidden'
              : error.status === 401
                ? 'session_expired'
                : 'server_error'
            : 'server_error';
        setState({ status: 'denied', reason });
      });
  }, []);

  if (state.status === 'loading') return null;
  if (state.status === 'denied') return <Navigate to={`/login?reason=${state.reason}`} replace />;
  return <AdminContext.Provider value={state.admin}>{children}</AdminContext.Provider>;
}

function RailItem({ to, label, end, children }: { to: string; label: string; end?: boolean; children: ReactNode }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => 'rail__item' + (isActive ? ' is-on' : '')}>
      {children}
      <span>{label}</span>
    </NavLink>
  );
}

function AdminMenu() {
  const admin = useAdmin();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div className="menu" ref={ref}>
      <button className="avatar" aria-label="Account" aria-haspopup="true" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {admin.initials}
      </button>
      {open && (
        <div className="menu__pop">
          <div className="menu__head">
            <b>{admin.name}</b>
            <span>{admin.email}</span>
          </div>
          <button onClick={() => void signOut().then(() => navigate('/login'))}>Sign out</button>
        </div>
      )}
    </div>
  );
}

export function AdminTopCrumb({ children }: { children: ReactNode }) {
  return (
    <header className="top">
      <div className="top__crumb">
        <span className="tag tag--bad">Tenpoint staff</span>
        <span className="sep">/</span>
        {children}
      </div>
      <div className="top__actions">
        <AdminMenu />
      </div>
    </header>
  );
}

export function AdminShell() {
  return (
    <RequireAdmin>
      <div className="app">
        <nav className="rail rail--staff" aria-label="Admin sections">
          <NavLink className="rail__brand" to="/admin" title="Tenpoint admin">
            <svg viewBox="0 0 32 32" aria-hidden="true">
              <polygon
                points="16.00,2.50 23.94,5.08 28.84,11.83 28.84,20.17 23.94,26.92 16.00,29.50 8.06,26.92 3.16,20.17 3.16,11.83 8.06,5.08"
                fill="#87ceeb"
              />
            </svg>
          </NavLink>

          <div className="rail__nav">
            <RailItem to="/admin" label="Organizations" end={false}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 21h18M5 21V8l7-4 7 4v13" />
                <path d="M9 12h2M13 12h2M9 16h6" />
              </svg>
            </RailItem>
            <RailItem to="/admin/users" label="Users">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
              </svg>
            </RailItem>
            <RailItem to="/admin/staff" label="Tenpoint">
              <svg viewBox="0 0 32 32" aria-hidden="true" stroke="none">
                <polygon
                  points="16.00,3.50 23.13,5.82 27.53,11.87 27.53,19.33 23.13,25.38 16.00,27.70 8.87,25.38 4.47,19.33 4.47,11.87 8.87,5.82"
                  fill="currentColor"
                />
              </svg>
            </RailItem>
            <RailItem to="/admin/settings" label="Settings">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </RailItem>
          </div>

          <div className="rail__foot" />
        </nav>

        <div className="main" style={{ opacity: 1 }}>
          <Outlet />
        </div>
      </div>
    </RequireAdmin>
  );
}
